import React, { useCallback, useEffect, useRef } from "react";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif", "heic"];
const VIDEO_EXTS = ["mp4", "webm", "ogg", "mov", "m4v"];

export function fileExtFromUrl(url) {
  try {
    const path = decodeURIComponent(String(url || "").split("?")[0].split("#")[0]);
    const base = path.split("/").pop() || "";
    const dot = base.lastIndexOf(".");
    return dot >= 0 ? base.slice(dot + 1).toLowerCase() : "";
  } catch {
    return "";
  }
}

export function mediaKind(url) {
  const ext = fileExtFromUrl(url);
  if (IMAGE_EXTS.includes(ext)) return "image";
  if (VIDEO_EXTS.includes(ext)) return "video";
  return null;
}

export function fileNameFromUrl(url) {
  try {
    return decodeURIComponent((String(url || "").split("?")[0].split("#")[0].split("/").pop() || "media"));
  } catch {
    return "media";
  }
}

export function collectChatMedia(messages = []) {
  const items = [];
  for (const m of messages) {
    if (m?.deletedAt) continue;
    for (const file of m.files || []) {
      const kind = mediaKind(file);
      if (!kind) continue;
      items.push({
        url: file,
        kind,
        name: fileNameFromUrl(file),
        messageId: m._id,
      });
    }
  }
  return items;
}

const navBtnSx = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 2,
  width: 48,
  height: 48,
  bgcolor: "rgba(255,255,255,0.14)",
  color: "#fff",
  backdropFilter: "blur(8px)",
  "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
};

export default function ChatMediaLightbox({ items = [], index = 0, onClose, onIndexChange }) {
  const open = items.length > 0 && index >= 0;
  const current = open ? items[index] : null;
  const touchX = useRef(null);
  const videoRef = useRef(null);

  const go = useCallback(
    (dir) => {
      if (!items.length) return;
      const next = (index + dir + items.length) % items.length;
      onIndexChange?.(next);
    },
    [index, items.length, onIndexChange]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        go(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        go(1);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, go]);

  useEffect(() => {
    const el = videoRef.current;
    return () => {
      if (el) {
        el.pause();
      }
    };
  }, [index]);

  if (!open || !current) return null;

  const many = items.length > 1;

  return (
    <Modal
      open
      onClose={onClose}
      sx={{ zIndex: 1500 }}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(8, 8, 16, 0.92)" } } }}
    >
      <Box
        onClick={onClose}
        onTouchStart={(e) => {
          touchX.current = e.changedTouches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
          touchX.current = null;
          if (dx > 56) go(-1);
          else if (dx < -56) go(1);
        }}
        sx={{
          outline: "none",
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          userSelect: "none",
        }}
      >
        <IconButton
          aria-label="Close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 2,
            bgcolor: "rgba(255,255,255,0.14)",
            color: "#fff",
            "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        {many && (
          <Typography
            sx={{
              position: "absolute",
              top: 22,
              left: "50%",
              transform: "translateX(-50%)",
              color: "rgba(255,255,255,0.85)",
              fontWeight: 600,
              fontSize: "0.85rem",
              letterSpacing: "0.04em",
            }}
          >
            {index + 1} / {items.length}
          </Typography>
        )}

        {many && (
          <IconButton
            aria-label="Previous"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            sx={{ ...navBtnSx, left: { xs: 8, sm: 20 } }}
          >
            <ChevronLeftIcon sx={{ fontSize: 32 }} />
          </IconButton>
        )}

        {many && (
          <IconButton
            aria-label="Next"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            sx={{ ...navBtnSx, right: { xs: 8, sm: 20 } }}
          >
            <ChevronRightIcon sx={{ fontSize: 32 }} />
          </IconButton>
        )}

        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            maxWidth: { xs: "92vw", md: "86vw" },
            maxHeight: "84vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            px: { xs: 6, sm: 8 },
          }}
        >
          {current.kind === "video" ? (
            <Box
              component="video"
              key={current.url}
              ref={videoRef}
              src={current.url}
              controls
              autoPlay
              playsInline
              sx={{
                maxWidth: "100%",
                maxHeight: "78vh",
                borderRadius: "12px",
                bgcolor: "#000",
                outline: "none",
              }}
            />
          ) : (
            <Box
              component="img"
              key={current.url}
              src={current.url}
              alt={current.name || "Shared media"}
              sx={{
                maxWidth: "100%",
                maxHeight: "78vh",
                objectFit: "contain",
                borderRadius: "12px",
                boxShadow: "0 16px 48px rgba(0,0,0,0.45)",
              }}
            />
          )}
          {current.name && (
            <Typography
              sx={{
                mt: 1.25,
                color: "rgba(255,255,255,0.72)",
                fontSize: "0.78rem",
                maxWidth: "80vw",
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {current.name}
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
